import { formatPostObject } from '../util/formatPostObject';
import { CompanyFormData, FormData, MSPFormData } from '../types';
import { FORM_TYPES } from '../consts';
import { getUserCheckEndpoint } from '../util/getExperimentVersion';
import { getCookie } from '../util/cookies';
import addExperimentVersionToUrl from '../util/successUrlHandler';
import { useAppStore } from '../store/appStore';
import { getRequestHeaders } from '../util/getRequestHeaders';
import { httpClient } from './httpClient';

export function postContactFormDataService(
  postObject: object,
  optout: boolean,
  textareaExistsStateVariable: boolean,
  signal?: AbortSignal
) {
  const { formType } = useAppStore.getState();
  const formattedPostObject = formatPostObject(postObject, optout, textareaExistsStateVariable);
  const determinedContactPostEndpoint = (() => {
    const form = document.getElementById('form');
    const isLegacyPartner = form.dataset?.type === 'partner';
    if (isLegacyPartner) {
      return 'SendPartnersDataAsync';
    } else if (formType === FORM_TYPES.PARTNER) {
      return 'SendPartnersInterest';
    } else {
      return 'SendAsync';
    }
  })();
  return httpClient
    .post(`/lpapi/form/${determinedContactPostEndpoint}`, formattedPostObject, { signal })
    .catch(() => {
      return false;
    })
    .then((data) => {
      return !!data;
    });
}

export const postTrialFormDataService = async (data: FormData | CompanyFormData, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post('/lpapi/trial/SubmitFormData', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const postMSPTrialFormDataService = async (data: MSPFormData, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post('/lpapi/trial/SubmitMspData', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const postTeamsTrialFormDataService = async (data: FormData, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post('/lpapi/trial/SubmitTeamsFormData', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const postTrialFormDataServiceLegacy = async (data: FormData, successUrl: string) => {
  try {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = addExperimentVersionToUrl(successUrl);

    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'email';
    emailInput.value = data.email;
    form.appendChild(emailInput);

    const contactPermission = document.createElement('input');
    contactPermission.type = 'hidden';
    contactPermission.name = 'contactPermission';
    contactPermission.value = data.contactPermission.toString();
    form.appendChild(contactPermission);

    const marketingOptOutShown = document.createElement('input');
    marketingOptOutShown.type = 'hidden';
    marketingOptOutShown.name = 'marketingOptOutShown';
    marketingOptOutShown.value = data.marketingOptOutShown.toString();
    form.appendChild(marketingOptOutShown);

    document.body.appendChild(form);
    form.submit();
  } catch (error) {
    return {
      success: false,
      code: 500,
    };
  }
};

export const postFamiliesTrialFormDataService = async (data: FormData, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post('/lpapi/trial/SubmitFamiliesFormData', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export function getSiteCoreContent(signal?: AbortSignal) {
  const { contentId, formType } = useAppStore.getState();

  const LPFormData = window.LPFormData;
  const localContentId = contentId
    ? contentId
    : typeof LPFormData === 'object'
      ? LPFormData.contentId
      : '{B4F20BDD-552E-4827-878C-71E0977D3ACD}';
  const cookieValue = getCookie('lastpass#lang');
  const langPathSection = cookieValue || 'en';
  if (
    formType === FORM_TYPES.TRIAL ||
    LPFormData?.formType === FORM_TYPES.TRIAL ||
    LPFormData?.formType === FORM_TYPES.DIRECTBUY
  ) {
    return httpClient.get(`/${langPathSection}/lpapi/trial/getfields/${localContentId}`, {
      signal,
      normalizeError: false,
    });
  } else {
    return httpClient.get(`/${langPathSection}/lpapi/form/fields/${localContentId}`, {
      signal,
      normalizeError: false,
    });
  }
}

export function getSiteCoreModalContent(contentId: string, signal?: AbortSignal) {
  const cookieValue = getCookie('lastpass#lang');
  const langPathSection = cookieValue || 'en';
  return httpClient.get(`/${langPathSection}/lpapi/trial/getfields/${contentId}`, {
    signal,
    normalizeError: false,
  });
}

export const getEmail = async (signal?: AbortSignal) => {
  try {
    const response = await httpClient.get('/lpapi/trial/gettrialemail', { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const checkBusinessUser = async (
  data: { flowType: string; trialLength: number; trialSku: string },
  signal?: AbortSignal
) => {
  try {
    const response = await httpClient.post('/lpapi/trial/EmailCheckBusinessUser', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getZoomInfoData = async (email: string, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post(
      '/lpapi/zoominfo/Contactv2',
      { email },
      { headers: getRequestHeaders(), signal }
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const checkUserValidity = async (
  data: {
    email: string;
    trialLength: number;
    trialSku: string;
    flowType?: string;
  },
  signal?: AbortSignal
) => {
  try {
    const response = await httpClient.post(`/lpapi/trial/${getUserCheckEndpoint()}`, data, {
      headers: getRequestHeaders(),
      signal,
    });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const retainFormData = async (data: FormData, signal?: AbortSignal) => {
  try {
    const response = await httpClient.post('/lpapi/trial/retainformdata', data, { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getFormData = async (signal?: AbortSignal) => {
  try {
    const response = await httpClient.get('/lpapi/trial/gettrialdatainfo', { signal });
    return response.data;
  } catch (error) {
    return error;
  }
};
